/**
 * WhereConnectorChip — the AND / OR chip between WHERE / HAVING conditions.
 *
 * When the owning boolean group is known (`groupPath`) and editing is wired,
 * the chip is a dropdown — `AND ▾` with a hover state, the same clickable
 * pattern as the `INNER JOIN ▾` chip — that switches the group's connector via
 * the `set-connector` edit (every connector of that group flips together, as
 * in the Rail view's ⇄ button). Without an owning group or edit context it
 * renders the plain label as before.
 */

import { ChevronDown } from 'lucide-react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { cn } from '../../../../../lib/utils';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { setGroupConnector } from '../../edit/edits/setGroupConnector';
import { VqeTooltip } from '../common/VqeTooltip';

type Connector = 'AND' | 'OR';

const CHOICES: ReadonlyArray<{ readonly value: Connector; readonly hint: string }> = [
  { value: 'AND', hint: 'Every condition must match' },
  { value: 'OR', hint: 'Any condition can match' },
];

export interface WhereConnectorChipProps {
  readonly connector: Connector;
  readonly testId: string;
  /** Chip colour / size classes (muted for top-level, tinted inside a group). */
  readonly className: string;
  readonly kind?: BooleanClauseTarget;
  /** Path of the boolean group this connector belongs to (`[]` = the clause root). */
  readonly groupPath?: readonly number[];
  readonly editContext?: VqeEditContext;
  /** Extra `data-connector` marker kept by the tinted in-group chips. */
  readonly markConnector?: boolean;
}

export function WhereConnectorChip({
  connector,
  testId,
  className,
  kind = 'where',
  groupPath,
  editContext,
  markConnector,
}: WhereConnectorChipProps) {
  const { track } = useAnalytics();
  if (!editContext || !groupPath) {
    return (
      <span
        className={className}
        data-testid={testId}
        data-connector={markConnector ? connector : undefined}
      >
        {connector}
      </span>
    );
  }
  const onSelect = (next: Connector): void => {
    if (next === connector) return;
    void setGroupConnector(groupPath, next, toCommitCtx(editContext), kind);
    track(`feature:vqe_${kind}_group_edit`, { action: 'connector' });
  };
  return (
    <DropdownMenu>
      <VqeTooltip content={`Change ${connector} to ${connector === 'AND' ? 'OR' : 'AND'}`}>
        <DropdownMenuTrigger
          type="button"
          aria-label={`Connector ${connector}, change`}
          data-testid={testId}
          data-connector={markConnector ? connector : undefined}
          data-editable="true"
          className={cn(
            className,
            'inline-flex cursor-pointer items-center gap-0.5 transition-colors hover:ring-1 hover:ring-ring/50 hover:brightness-95',
          )}
        >
          {connector}
          <ChevronDown className="h-3 w-3" aria-hidden />
        </DropdownMenuTrigger>
      </VqeTooltip>
      <DropdownMenuContent align="start" data-testid={`${testId}-menu`}>
        {CHOICES.map((choice) => (
          <DropdownMenuItem
            key={choice.value}
            data-testid={`${testId}-option-${choice.value.toLowerCase()}`}
            onSelect={() => onSelect(choice.value)}
            className={choice.value === connector ? 'font-semibold' : undefined}
          >
            <div className="flex flex-col gap-0.5">
              <span>{choice.value}</span>
              <span className="text-[10px] font-normal text-muted-foreground">{choice.hint}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
